import { SPEAKER_SAMPLE_RATE, encodeSpeakerPcm16Le, resampleSpeakerAudio } from './speaker-audio'

/**
 * Live (push-to-talk) announcements over the DJI Cloud API.
 *
 * The AS1 speaker only accepts complete PCM files through
 * speaker_audio_play_start; there is no audio-streaming service. Live voice is
 * therefore relayed as a chain of short PCM segments: the microphone is
 * buffered, cut into segments, and each segment is uploaded as soon as the
 * previous one has finished playing. The pure buffering / flushing rules live
 * here so they can be unit tested without a browser.
 */

/** Wait for at least this much audio before sending a segment. */
export const LIVE_SEGMENT_MIN_MS = 2000
/** Never send more than this in one segment (keeps per-file latency bounded). */
export const LIVE_SEGMENT_MAX_MS = 20000
/** Drop the oldest audio once the backlog grows past this (slow link / speaker). */
export const LIVE_BACKLOG_LIMIT_MS = 45000
/** Peak amplitude below which a segment counts as silence and is skipped. */
export const LIVE_VOICE_THRESHOLD = 0.012
/** Extra time granted for download / encode before a stuck segment is released. */
export const LIVE_PLAYBACK_GRACE_MS = 12000

export interface LiveFlushState {
  bufferedMs: number
  awaitingPlayback: boolean
  /** The operator stopped talking: send whatever is left, even if short. */
  finishing?: boolean
}

export interface LiveFlushOptions {
  minSegmentMs?: number
  maxSegmentMs?: number
  backlogLimitMs?: number
}

export type LiveFlushDecision =
  | { action: 'wait' }
  | { action: 'send', segmentMs: number }
  | { action: 'trim', keepMs: number }

export interface LiveSegment {
  blob: Blob
  durationMs: number
  fileName: string
  index: number
}

export class LiveSpeakerBuffer {
  readonly sampleRate: number
  private chunks: Float32Array[] = []
  private sampleCount = 0

  constructor (sampleRate: number = SPEAKER_SAMPLE_RATE) {
    this.sampleRate = sampleRate
  }

  get durationMs () {
    return Math.round((this.sampleCount / this.sampleRate) * 1000)
  }

  push (chunk: Float32Array) {
    if (!chunk || chunk.length === 0) {
      return
    }
    this.chunks.push(chunk)
    this.sampleCount += chunk.length
  }

  /** Remove and return up to maxMs of the oldest audio. */
  take (maxMs: number): Float32Array {
    const wanted = Math.min(this.sampleCount, Math.max(0, Math.floor((maxMs / 1000) * this.sampleRate)))
    const result = new Float32Array(wanted)
    let offset = 0
    while (offset < wanted && this.chunks.length > 0) {
      const head = this.chunks[0]
      const remaining = wanted - offset
      if (head.length <= remaining) {
        result.set(head, offset)
        offset += head.length
        this.chunks.shift()
      } else {
        result.set(head.subarray(0, remaining), offset)
        this.chunks[0] = head.subarray(remaining)
        offset += remaining
      }
    }
    this.sampleCount -= wanted
    return result
  }

  /** Drop the oldest audio so that only keepMs remains; returns the dropped duration. */
  trimTo (keepMs: number): number {
    const excessMs = this.durationMs - Math.max(0, keepMs)
    if (excessMs <= 0) {
      return 0
    }
    this.take(excessMs)
    return excessMs
  }

  clear () {
    this.chunks = []
    this.sampleCount = 0
  }
}

export function measurePeakLevel (chunk: Float32Array): number {
  let peak = 0
  for (let index = 0; index < chunk.length; index += 1) {
    const value = Math.abs(chunk[index])
    if (value > peak) {
      peak = value
    }
  }
  return Math.min(1, peak)
}

export function hasVoice (samples: Float32Array, threshold = LIVE_VOICE_THRESHOLD): boolean {
  return measurePeakLevel(samples) >= threshold
}

export function resolveLiveFlush (state: LiveFlushState, options: LiveFlushOptions = {}): LiveFlushDecision {
  const minSegmentMs = options.minSegmentMs ?? LIVE_SEGMENT_MIN_MS
  const maxSegmentMs = options.maxSegmentMs ?? LIVE_SEGMENT_MAX_MS
  const backlogLimitMs = options.backlogLimitMs ?? LIVE_BACKLOG_LIMIT_MS
  if (state.bufferedMs > backlogLimitMs) {
    return { action: 'trim', keepMs: backlogLimitMs }
  }
  if (state.awaitingPlayback || state.bufferedMs <= 0) {
    return { action: 'wait' }
  }
  if (state.finishing || state.bufferedMs >= minSegmentMs) {
    return { action: 'send', segmentMs: Math.min(state.bufferedMs, maxSegmentMs) }
  }
  return { action: 'wait' }
}

export function buildLiveSegment (samples: Float32Array, sourceSampleRate: number, index: number): LiveSegment {
  const resampled = sourceSampleRate === SPEAKER_SAMPLE_RATE ? samples : resampleSpeakerAudio(samples, sourceSampleRate)
  const pcm = encodeSpeakerPcm16Le(resampled)
  return {
    blob: new Blob([pcm], { type: 'application/octet-stream' }),
    durationMs: Math.max(1, Math.round((resampled.length / SPEAKER_SAMPLE_RATE) * 1000)),
    fileName: `live-${String(index).padStart(3, '0')}.pcm`,
    index,
  }
}

/** Time to wait for one segment to finish before assuming the confirmation was lost. */
export function livePlaybackTimeoutMs (segmentMs: number): number {
  return Math.max(1, segmentMs) + LIVE_PLAYBACK_GRACE_MS
}

export function describeLiveStatus (state: {
  active: boolean
  starting?: boolean
  awaitingPlayback: boolean
  bufferedMs: number
  sentSegments: number
}): string {
  if (state.starting) {
    return '正在打开麦克风…'
  }
  if (!state.active) {
    return state.sentSegments > 0 ? `实时喊话已结束，共发送 ${state.sentSegments} 段` : '未开始'
  }
  const buffered = `待发送 ${(state.bufferedMs / 1000).toFixed(1)} s`
  if (state.awaitingPlayback) {
    return `喊话器播放中 · 已发送 ${state.sentSegments} 段 · ${buffered}`
  }
  return state.sentSegments > 0
    ? `采集中 · 已发送 ${state.sentSegments} 段 · ${buffered}`
    : `采集中 · ${buffered}`
}
